import { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { useApi } from '../hooks/useApi';

/** Comprobante de un grupo de posibles duplicados (GET facturas/posibles-duplicados o facturas-venta/posibles-duplicados). */
interface ComprobanteDuplicado {
  id: number;
  tipo?: string | null;
  numero: string;
  proveedor?: string | null;
  cliente?: string | null;
  fechaEmision: string;
  montoTotal: number;
  estado: string;
}
interface GrupoDuplicados { comprobantes: ComprobanteDuplicado[] }

const fechaCorta = (f: string) => String(f).slice(0, 10).split('-').reverse().join('/');
const pesos = (n: number) => `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Botón "Posibles duplicados" y su modal: grupos de comprobantes que el backend considera el mismo (misma clase,
 * letra, punto de venta y número, aunque el número esté escrito distinto). Solo lectura.
 */
export function PosiblesDuplicados({ endpoint, contraparte }: { endpoint: string; contraparte: 'proveedor' | 'cliente' }) {
  const [open, setOpen] = useState(false);
  // Se consulta al abrir (y de nuevo en cada apertura)
  const { data, loading, error } = useApi<GrupoDuplicados[]>(open ? endpoint : null);
  const grupos = data ?? [];
  const titulo = contraparte === 'proveedor' ? 'Proveedor' : 'Cliente';

  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} title="Comprobantes cargados más de una vez">
        Posibles duplicados
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Posibles duplicados" size="lg">
        {loading ? (
          <p className="text-sm text-slate-500">Buscando comprobantes duplicados...</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : grupos.length === 0 ? (
          <p className="text-sm text-slate-500">No hay comprobantes duplicados</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Cada grupo reúne comprobantes con el mismo número, aunque esté escrito distinto
              {contraparte === 'proveedor' ? ' (del mismo proveedor)' : ' (de cualquier cliente)'}.
            </p>
            {grupos.map((g) => (
              <div key={g.comprobantes.map((c) => c.id).join('-')} className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 bg-slate-50">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Comprobante</th>
                      <th className="px-3 py-2">{titulo}</th>
                      <th className="px-3 py-2">Emisión</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.comprobantes.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="px-3 py-2">{c.id}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{c.numero}</td>
                        <td className="px-3 py-2">{(contraparte === 'proveedor' ? c.proveedor : c.cliente) || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(c.fechaEmision)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{pesos(c.montoTotal)}</td>
                        <td className="px-3 py-2">{c.estado}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
